<template>
  <div class="content-search p-0">
    <div class="flex-container-highlight-radio">
      <base-switch
        v-model="removeSignal"
        type="secondary"
        @input="toggleMode"
      />
      <h5 class="flex-child-highlight-radio">
        Highlight in text
      </h5>
    </div>
    <div class="flex-container-highlight-radio">
      <base-switch
        v-model="includeSearch"
        type="secondary"
      />
      <h5
        class="flex-child-highlight-radio"
        style="color: #4cbb17"
      >
        Include search words
      </h5>
    </div>
    <div class="flex-container-highlight-radio">
      <base-switch
        v-model="includeCaseKW"
        type="secondary"
      />
      <h5
        class="flex-child-highlight-radio"
        style="color: yellow"
      >
        Include case keywords
      </h5>
    </div>
    <div class="flex-container-highlight-radio">
      <base-switch
        v-model="includeCodeWords"
        type="secondary"
      />
      <h5
        class="flex-child-highlight-radio"
        style="color: red"
      >
        Include code words
      </h5>
    </div>
    <h4>Signals</h4>
    <div>
      <span v-if="sigMatches.length === 0">No Matches Found</span>
      <span
        v-for="(sigMatch, ix) in sigMatches"
        :key="sigMatch.groupName + '-' + ix"
        class="cursor-pointer d-block text-100"
        @click="goTo(index)"
      >
        <template v-if="sigMatch.groupName === 'Search Word' || sigMatch.groupName === 'Case Keyword'">
          {{ sigMatch.groupName + ' / ' + sigMatch.word + ' ( ' + sigMatch.matches.length + ' )' }}
        </template>
        <template v-else>
          {{ sigMatch.groupName + ' / ' + sigMatch.collectionName + ' / '
            + sigMatch.word + ' ( ' + sigMatch.matches.length + ' )' }}
        </template>
      </span>
    </div>
  </div>
</template>

<script>
import {mapGetters, mapMutations} from "vuex";
import {getCodeWords, getCodeWordGroups} from "../../../../api";
import {TextSearchMatcher} from "../../../../util/textSearch";

export default {
  name: "transcription-signals",
  props: {
    caseId: {
      type: Number,
      default: 0,
    },
    transcriptContent: {
      type: String,
      default: "",
    },
    translationContent: {
      type: String,
      default: "",
    },
    metadata: {
      type: Object,
      default: null,
    },
    filterTerm: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      current: -1,
      removeSignal: true,
      includeSearch: true,
      includeCaseKW: true,
      includeCodeWords: true,
    };
  },
  computed: {
    ...mapGetters("data", [
      "codeWordGroups",
      "getCaseSummaries",
      "highlightSignalsInText",
      "includeSearchWordSignals",
      "includeCaseKeywordSignals",
      "includeCodeWordSignals",
    ]),
    sigMatches() {
      const matches = [];

      // Search transcript content for hits on the filter term
      if (this.filterTerm && this.includeSearch) {
        const searchWordMatches = this.findMatches(this.transcriptContent, "lhs", "Search Word", null, this.filterTerm);
        this.mergeMatches(searchWordMatches, matches);
      }

      // Search translation content for hits on the filter term
      if (this.translationContent && this.filterTerm && this.includeSearch) {
        const searchWordMatches = this.findMatches(this.translationContent, "rhs", "Search Word", null, this.filterTerm);
        this.mergeMatches(searchWordMatches, matches);
      }

      // Search transcript content for hits on case keywords
      if (this.caseKeywords.length > 0 && this.includeCaseKW) {
        const kwTerm = this.caseKeywords.join(" ");
        const caseKwMatches = this.findMatches(this.transcriptContent, "lhs", "Case Keyword", null, kwTerm);
        this.mergeMatches(caseKwMatches, matches);
      }

      // Search translation content for hits on case keywords
      if (this.translationContent && this.caseKeywords.length > 0 && this.includeCaseKW) {
        const kwTerm = this.caseKeywords.join(" ");
        const caseKwMatches = this.findMatches(this.translationContent, "rhs", "Case Keyword", null, kwTerm);
        this.mergeMatches(caseKwMatches, matches);
      }

      // Search transcript content for hits on code word groups
      if (this.includeCodeWords) {
        const entries = this.codeWordGroups ? Object.entries(this.codeWordGroups) : [];
        if (entries.length > 0) {
          entries.forEach((g) => {
            const val = g[1];
            if (val.collections && val.collections.length > 0) {
              val.collections.forEach((c) => {
                if (c.words && c.words.length > 0) {
                  c.words.forEach((w) => {
                    const codeWordMatches = this.findMatches(this.transcriptContent, "lhs", val.name, c.name, w.phrase);
                    this.mergeMatches(codeWordMatches, matches);
                  });
                }
              });
            }
          });
        }
      }

      // Search translation content for hits on code word groups
      if (this.translationContent && this.includeCodeWords) {
        const entries = this.codeWordGroups ? Object.entries(this.codeWordGroups) : [];
        if (entries.length > 0) {
          entries.forEach((g) => {
            const val = g[1];
            if (val.collections && val.collections.length > 0) {
              val.collections.forEach((c) => {
                if (c.words && c.words.length > 0) {
                  c.words.forEach((w) => {
                    const codeWordMatches = this.findMatches(this.translationContent, "rhs", val.name, c.name, w.phrase);
                    this.mergeMatches(codeWordMatches, matches);
                  });
                }
              });
            }
          });
        }
      }

      this.$emit("signalMatches", matches);
      console.log("Signal matches", matches);
      return matches;
    },
    caseKeywords() {
      const caseSummary = this.getCaseSummaries.data.find((s) => s.id == this.caseId);
      if (caseSummary) {
        return caseSummary.caseKeywords.map((c) => c.word);
      }
      return [];
    },
  },
  mounted() {
    const entries = this.codeWordGroups ? Object.entries(this.codeWordGroups) : [];
    if (entries.length < 1) {
      getCodeWordGroups().then((groups) => {
        this.putCodeWordGroups(groups);
        groups.forEach((g) => this.loadGroup(g.id));
      }).catch((ex) => {
        this.$notifyError("Failed to get Groups", ex);
      });
    }
    this.removeSignal = this.highlightSignalsInText;
    this.includeSearch = this.includeSearchWordSignals;
    this.includeCaseKW = this.includeCaseKeywordSignals;
    this.includeCodeWords = this.includeCodeWordSignals;
  },
  methods: {
    ...mapMutations("data", [
      "putCodeWordGroups",
      "putCodeWordGroup",
    ]),
    loadGroup(groupId) {
      getCodeWords(groupId).then((group) => {
        this.putCodeWordGroup(group);
      }).catch((ex) => {
        this.$notifyError("Failed to load Code Word details", ex);
      });
    },
    findMatches(content, side, groupName, collectionName, searchTerm) {
      const matches = [];
      const searchMatches = TextSearchMatcher.findMatches(content, searchTerm);
      if (searchMatches && searchMatches.length > 0) {
        searchMatches.sort((a, b) => a.searchTerm.localeCompare(b.searchTerm));
        let tempIndices = [];
        let tempLengths = [];
        let lastWord = searchMatches[0].searchTerm;
        searchMatches.forEach((m) => {
          if (m.searchTerm != lastWord) {
            const matchObjs = tempIndices.map((tempIdx, ix) => {
              return {
                side: side,
                index: tempIdx,
                length: tempLengths[ix],
              };
            });
            matches.push({
              groupName: groupName,
              collectionName: collectionName,
              word: lastWord,
              matches: matchObjs,
            });
            tempIndices = [];
            tempLengths = [];
            lastWord = m.searchTerm;
          }
          tempIndices.push(m.offset);
          tempLengths.push(m.length);
        });
        const matchObjs = tempIndices.map((tempIdx, ix) => {
          return {
            side: side,
            index: tempIdx,
            length: tempLengths[ix],
          };
        });
        matches.push({
          groupName: groupName,
          collectionName: collectionName,
          word: lastWord,
          matches: matchObjs,
        });
      }
      return matches;
    },
    mergeMatches(newMatches, oldMatches) {
      newMatches.forEach((nm) => {
        const match = oldMatches.find((om) => om.groupName === nm.groupName &&
          om.collectionName === nm.collectionName && om.word === nm.word);
        if (match) {
          match.matches.push(...nm.matches);
        } else {
          oldMatches.push(nm);
        }
      });
    },
    previous() {
      this.setCurrent(this.current - 1);
    },
    next() {
      this.setCurrent(this.current + 1);
    },
    setCurrent(index) {
      this.current = Math.max(0, Math.min(this.indices.length - 1, index));
      this.$emit("current", this.current);
    },
    goTo(index) {
      const match = this.metadata && this.metadata.entries && this.metadata.entries
        .find((m) => m.s <= index.indices[0] && index.indices[0] <= m.e);
      if (match) {
        const key = `${match.s}-${match.e}`;
        const el = document.querySelector(`span[data-key="${key}"`);
        if (el) {
          el.scrollIntoView();
        }
      }
    },
    toggleMode(type) {
      this.$emit("removeSignal", this.removeSignal);
    },
  },
};
</script>
